import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';

const MemberProfile = () => {
  const { t } = useTranslation();
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { user, getUserGroups } = useAuth();
  
  const [member, setMember] = useState(null);
  const [memberEvents, setMemberEvents] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participationLoading, setParticipationLoading] = useState({});

  useEffect(() => {
    fetchMemberData();
    fetchUserGroups();
  }, [memberId]);

  const fetchUserGroups = async () => {
    try {
      const result = await getUserGroups();
      if (result.success) {
        setUserGroups(result.groups);
      }
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
    }
  };

  const fetchMemberData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Fetch member profile
      const memberResponse = await fetch(`/api/users/${memberId}`, { headers });
      if (!memberResponse.ok) {
        throw new Error('Failed to fetch member profile');
      }
      const memberData = await memberResponse.json();
      setMember(memberData);

      // Fetch member's events
      const eventsResponse = await fetch(`/api/users/${memberId}/events`, { headers });
      if (!eventsResponse.ok) {
        throw new Error('Failed to fetch member events');
      }
      const eventsData = await eventsResponse.json();
      setMemberEvents(eventsData.events || []);
      
    } catch (error) {
      console.error('Error fetching member data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const isUserParticipating = (event) => {
    return event.participants && event.participants.includes(user.id);
  };

  const isUserMemberOfGroup = (groupTitle) => {
    return userGroups.some(group => group.title === groupTitle);
  };

  const handleJoinEvent = async (eventId) => {
    setParticipationLoading(prev => ({ ...prev, [eventId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/events/${eventId}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to join event');
      }
      
      // Update local event state
      setMemberEvents(prevEvents => 
        prevEvents.map(event => 
          event.id === eventId 
            ? { ...event, participants: [...(event.participants || []), user.id] }
            : event
        )
      );
      
    } catch (error) {
      console.error('Error joining event:', error);
      alert(error.message);
    } finally {
      setParticipationLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const handleLeaveEvent = async (eventId) => {
    setParticipationLoading(prev => ({ ...prev, [eventId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/events/${eventId}/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to leave event');
      }
      
      // Update local event state
      setMemberEvents(prevEvents => 
        prevEvents.map(event => 
          event.id === eventId 
            ? { ...event, participants: (event.participants || []).filter(id => id !== user.id) }
            : event
        )
      );
      
    } catch (error) {
      console.error('Error leaving event:', error);
      alert(error.message);
    } finally {
      setParticipationLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return timeString?.substring(0, 5);
  };

  const renderEventCard = (event) => (
    <div key={event.id} className="event-card member-event-card">
      {event.groupLogoUrl && (
        <div className="event-logo">
          <img src={event.groupLogoUrl} alt="Group logo" />
        </div>
      )}
      
      <div className="event-header">
        <h3>{event.title}</h3>
        {event.groupTitle && isUserMemberOfGroup(event.groupTitle) && (
          <div className="event-group-name">{event.groupTitle}</div>
        )}
      </div>
      
      <div className="event-details">
        <div className="event-datetime">
          {formatDate(event.date)} {formatTime(event.time)}
        </div>
        {event.description && (
          <p>{event.description}</p>
        )}
        
        <div className="event-participation">
          {isUserParticipating(event) ? (
            <button
              onClick={() => handleLeaveEvent(event.id)}
              disabled={participationLoading[event.id]}
              className="btn btn-secondary btn-full"
            >
              {participationLoading[event.id] ? (t('events.leaving') || 'Wird verlassen...') : (t('events.dontParticipate') || 'Ich nehme nicht teil')}
            </button>
          ) : (
            <button
              onClick={() => handleJoinEvent(event.id)}
              disabled={participationLoading[event.id]}
              className="btn btn-primary btn-full"
            >
              {participationLoading[event.id] ? (t('events.joining') || 'Wird beigetreten...') : (t('events.participate') || 'Ich nehme teil')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="container">
        <div className="error-message">Member not found</div>
      </div>
    );
  }

  return (
    <div className="container member-profile-page">
      <div className="member-profile-header">
        <div className="member-profile-avatar">
          {member.photoUrl ? (
            <img src={member.photoUrl} alt={member.name} />
          ) : (
            <div className="member-avatar-placeholder large">
              {member.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="member-profile-info">
          <h1>{member.name}</h1>
        </div>
      </div>
      
      <div className="member-events-section">
        {memberEvents.length === 0 ? (
          <div className="no-events">
            {t('members.noEvents')}
          </div>
        ) : (
          <div className="events-grid">
            {memberEvents.map(renderEventCard)}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberProfile;
